import { z } from 'zod';
import { trimmed } from './_fields.js';
import {
  integrationAppointmentParamSchema,
  integrationAssignSchema,
  integrationJobParamSchema,
  type IntegrationAssignInput,
  type IntegrationAssignResultDto,
  type IntegrationErrorJobDto,
  type IntegrationErrorListDto,
  type IntegrationStreamStatusDto,
  type IntegrationUnmatchedAppointmentDto,
  type IntegrationUnmatchedListDto,
} from './integrations-sync.js';
import { dateOnlySchema } from './patients.js';

/**
 * Docalia — la seconde plateforme de prise de rendez-vous du marché luxembourgeois.
 *
 * Ce fichier porte tout ce que le front et le serveur partagent de l'intégration. Il est réexporté
 * par `dto/integrations.ts`, qui reste le point d'entrée unique du domaine.
 *
 * **Aucun identifiant n'apparaît ici.** La clé d'API est un identifiant au sens de
 * `integrationCredentialsSchema` : chiffrée, jamais renvoyée.
 *
 * **Ce qui ressemble à Doctena et ce qui ne lui ressemble pas.** Les deux plateformes partagent
 * l'infrastructure — connexion, santé par flux, liens, journal, disjoncteur — et rien de leur
 * protocole. Docalia s'authentifie par une seule clé, associe des **professionnels** et non des
 * calendriers, n'a pas de flux incrémental à curseur mais une lecture par **fenêtre de dates**
 * ancrée sur une date de début d'import, et laisse le cabinet choisir ses règles de rapprochement.
 */

/* -------------------------------------------------------------------------- */
/* Réglages                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Environnements publiés par Docalia. **Liste fermée, et c'est le point.**
 *
 * L'écran de la V1 offrait un champ « URL de base » libre, avec une valeur par défaut pointant
 * vers un hébergement de développement (`docs/SCREENS.md`, onglet Docalia). Laisser un cabinet
 * écrire l'hôte que le serveur va appeler, c'est lui laisser choisir la destination d'une requête
 * sortante. Même décision que pour Doctena : le réglage nomme un environnement, la passerelle
 * seule connaît l'hôte, et aucune URL libre n'entre jamais en base.
 */
export const docaliaEnvironments = ['sandbox', 'production'] as const;
export type DocaliaEnvironment = (typeof docaliaEnvironments)[number];

/** Cadences proposées par l'écran. Bornes reprises telles quelles de la spécification. */
export const DOCALIA_SYNC_INTERVAL_MIN_MINUTES = 1;
export const DOCALIA_SYNC_INTERVAL_MAX_MINUTES = 60;

/**
 * Règles de rapprochement automatique des patients, telles que l'écran les expose — quatre
 * interrupteurs, enregistrés chacun immédiatement.
 *
 * `bySocialSecurityNumber` est **accepté et sans effet**, et le dire vaut mieux que le taire :
 * Docalia n'expose pas de matricule luxembourgeois, et rapprocher sur un identifiant étranger
 * relierait deux personnes sans lien. Le jour où le champ existe, c'est ici et dans
 * `sync.match.ts` que la règle s'ajoute — pas dans un écran.
 */
export const docaliaMatchingSchema = z
  .object({
    byEmail: z.boolean(),
    bySocialSecurityNumber: z.boolean(),
    byNameAndBirthDate: z.boolean(),
    /** Vrai = aucun rattachement automatique : tout passe par la boîte de réception. */
    requireManualValidation: z.boolean(),
  })
  .strict();
export type DocaliaMatching = z.infer<typeof docaliaMatchingSchema>;

export const docaliaSettingsSchema = z
  .object({
    environment: z.enum(docaliaEnvironments),
    syncIntervalMinutes: z
      .int()
      .min(DOCALIA_SYNC_INTERVAL_MIN_MINUTES)
      .max(DOCALIA_SYNC_INTERVAL_MAX_MINUTES),
    /**
     * La synchronisation Docalia est bidirectionnelle par nature — l'écran le rappelle par une
     * alerte permanente. Le drapeau reste explicite : à faux, rien ne repart, et le cabinet sait
     * ce qu'il a choisi plutôt que de le déduire d'un comportement.
     */
    bidirectionalSync: z.boolean(),
    /**
     * Date de début d'import historique. **La changer est destructif** : le curseur repart de
     * cette date et l'agenda est relu en entier, ce que l'écran annonce avant de confirmer.
     */
    historyStartDate: dateOnlySchema,
    /** Vrai = un rendez-vous sans dossier correspondant crée le dossier ; faux = boîte de réception. */
    autoCreatePatients: z.boolean(),
    matching: docaliaMatchingSchema,
  })
  .strict();
export type DocaliaSettings = z.infer<typeof docaliaSettingsSchema>;

/* -------------------------------------------------------------------------- */
/* Flux                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Deux flux suivis dans `integration_health`, contre trois chez Doctena : Docalia ne publie pas de
 * flux de patients séparé — ils arrivent attachés à leurs rendez-vous.
 */
export const docaliaStreams = ['appointments', 'push'] as const;
export type DocaliaStream = (typeof docaliaStreams)[number];

/* -------------------------------------------------------------------------- */
/* Correspondances de professionnels                                           */
/* -------------------------------------------------------------------------- */

/** Un professionnel Docalia, tel que l'écran d'association le présente. */
export interface DocaliaProfessionalDto {
  professionalId: string;
  title: string | null;
  firstName: string;
  lastName: string;
  specialty: string | null;
  /** Membre du cabinet déjà associé, ou nul. Les autres sont désactivés dans le sélecteur. */
  practitionerMemberId: string | null;
}

export interface DocaliaProfessionalListDto {
  professionals: DocaliaProfessionalDto[];
}

/**
 * Enregistrement en bloc : la liste reçue **remplace** l'existante, comme pour Doctena. Un
 * professionnel ne va qu'à un praticien, et un praticien ne reçoit qu'un professionnel — sans quoi
 * le tirage dupliquerait chaque rendez-vous.
 */
export const docaliaProfessionalLinksSchema = z
  .object({
    links: z
      .array(z.object({ professionalId: trimmed(64), practitionerMemberId: z.uuid() }).strict())
      .max(100),
  })
  .strict();
export type DocaliaProfessionalLinksInput = z.infer<typeof docaliaProfessionalLinksSchema>;

/* -------------------------------------------------------------------------- */
/* État                                                                        */
/* -------------------------------------------------------------------------- */

/** Santé d'un flux Docalia. La structure est commune ; seule la liste des flux est à lui. */
export type DocaliaStreamStatusDto = IntegrationStreamStatusDto<DocaliaStream>;

export interface DocaliaStatusDto {
  isEnabled: boolean;
  hasCredentials: boolean;
  settings: DocaliaSettings | null;
  /** Professionnels associés à un praticien. Zéro = rien ne sera jamais tiré. */
  linkedProfessionals: number;
  unmatchedCount: number;
  failedJobCount: number;
  /** Vrai tant qu'aucun tirage n'a réussi : le prochain repartira de la date d'import. */
  needsInitialSync: boolean;
  pushPausedCount: number;
  /**
   * Rendez-vous importés, à venir, qui en chevauchent un autre : importés quand même et signalés
   * « conflit à régler » dans l'agenda (décision du 30 septembre 2026).
   */
  conflictCount: number;
  streams: DocaliaStreamStatusDto[];
}

/* -------------------------------------------------------------------------- */
/* Déclenchement                                                               */
/* -------------------------------------------------------------------------- */

export interface DocaliaSyncResultDto {
  created: number;
  updated: number;
  cancelled: number;
  unmatched: number;
  pushed: number;
  errors: number;
}

/**
 * Boîtes de réception et paramètres de route : **exactement ceux de Doctena**, parce qu'ils ne
 * décrivent pas un protocole mais nos propres tables. Les contrats vivent dans
 * `dto/integrations-sync.ts` ; ces alias donnent au front les noms qu'il attend d'un module
 * Docalia, sans qu'une seconde définition puisse un jour diverger de la première.
 */
export const docaliaAppointmentParamSchema = integrationAppointmentParamSchema;
export const docaliaJobParamSchema = integrationJobParamSchema;
export const docaliaAssignSchema = integrationAssignSchema;
export type DocaliaAssignInput = IntegrationAssignInput;
export type DocaliaAssignResultDto = IntegrationAssignResultDto;
export type DocaliaUnmatchedAppointmentDto = IntegrationUnmatchedAppointmentDto;
export type DocaliaUnmatchedListDto = IntegrationUnmatchedListDto;
export type DocaliaErrorJobDto = IntegrationErrorJobDto;
export type DocaliaErrorListDto = IntegrationErrorListDto;
